/**
 * Maps: every map on the network as a tile, and blocking/unblocking (staff and above, with a linked
 * Minecraft account — the server enforces both).
 *
 * A blocked map shows the red BLOCKED tile that players see in game, with the real art behind a
 * "Show" toggle; the detail view shows the two side by side. Pictures load from the PNG endpoint and
 * are drawn pixelated, so they stay sharp when scaled up.
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NotAuthorizedError } from "../api";
import { formatRelative, formatTimestamp } from "../format";
import { can, type Tier } from "../types";
import { confirmDialog } from "./Dialog";

interface MapRow {
  uuid: string;
  blocked: boolean;
  blockedBy: { uuid: string; name: string } | null;
  blockedAt: number | null;
  createdAt: number | null;
  updatedAt: number | null;
  creator: { uuid: string | null; name: string };
  createdServer: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin" });
  if (response.status === 403) throw new NotAuthorizedError();
  const json = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(json.error ?? `HTTP ${response.status}`);
  return json;
}

const imageUrl = (map: MapRow): string => `/api/maps/${map.uuid}/image.png?v=${map.updatedAt ?? 0}`;

/** The map's art, or "No image saved" when there is none. */
function MapImage({ map, size }: { map: MapRow; size: number }): JSX.Element {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [map.uuid, map.updatedAt]);
  if (failed) {
    return (
      <div className="map-art map-empty" style={{ width: size, height: size }}>
        No image saved
      </div>
    );
  }
  return (
    <img
      className="map-art"
      src={imageUrl(map)}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/** What players see in game for a blocked map. */
function BlockedArt({ size }: { size: number }): JSX.Element {
  return (
    <div className="map-art map-blocked-art" style={{ width: size, height: size }}>
      BLOCKED
    </div>
  );
}

function MapTile({ map, onOpen }: { map: MapRow; onOpen: () => void }): JSX.Element {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="map-tile" data-blocked={map.blocked}>
      <button type="button" className="map-tile-art" onClick={onOpen} aria-label="Open map">
        {map.blocked && !reveal ? <BlockedArt size={192} /> : <MapImage map={map} size={192} />}
      </button>
      <div className="map-tile-info">
        <b>{map.creator.name}</b>
        <span className="users-note">
          {map.createdServer ?? "Unknown server"} · {map.createdAt ? formatRelative(map.createdAt) : "Unknown"}
        </span>
        {map.blocked && (
          <span className="map-tile-row">
            <span className="map-badge">Blocked</span>
            <button type="button" className="btn" onClick={() => setReveal((r) => !r)}>
              {reveal ? "Hide" : "Show"}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

export function MapsTab({
  tier,
  csrfToken,
  onOpenPlayer,
}: {
  tier: Tier;
  csrfToken: string;
  /** Opens a Minecraft uuid's profile page; absent when this account cannot open profiles. */
  onOpenPlayer?: (uuid: string) => void;
}): JSX.Element {
  const [filter, setFilter] = useState<"all" | "blocked" | "unblocked">("all");
  const [sort, setSort] = useState<"newest" | "blocked">("newest");
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [more, setMore] = useState<MapRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQ(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  const params = new URLSearchParams({ filter, sort });
  if (q) params.set("q", q);
  const query = params.toString();

  const first = useQuery({
    queryKey: ["maps", query],
    queryFn: () => getJson<{ maps: MapRow[]; nextCursor: string | null }>(`/api/maps?${query}`),
  });
  useEffect(() => {
    setMore([]);
    setCursor(first.data?.nextCursor ?? null);
  }, [first.data]);
  const maps = [...(first.data?.maps ?? []), ...more];

  const loadMore = async (): Promise<void> => {
    if (!cursor) return;
    const next = await getJson<{ maps: MapRow[]; nextCursor: string | null }>(`/api/maps?${query}&cursor=${encodeURIComponent(cursor)}`);
    setMore((m) => [...m, ...next.maps]);
    setCursor(next.nextCursor);
  };

  return (
    <div className="maps">
      <div className="filterbar maps-filters">
        <div className="portal-tabs">
          {(["all", "blocked", "unblocked"] as const).map((f) => (
            <button key={f} type="button" className="btn" data-active={filter === f} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : f === "blocked" ? "Blocked" : "Not blocked"}
            </button>
          ))}
        </div>
        <select className="input" value={sort} onChange={(e) => setSort(e.target.value as "newest" | "blocked")} aria-label="Sort">
          <option value="newest">Newest first</option>
          <option value="blocked">Most recently blocked</option>
        </select>
        <input
          className="input"
          placeholder="Creator name or map UUID"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Search maps"
          style={{ width: "28ch" }}
        />
        {first.isFetching && <span className="profile-spinner" aria-label="Loading" />}
      </div>

      {first.error && <div className="login-error">{first.error instanceof Error ? first.error.message : "Could not load maps."}</div>}
      {first.data && maps.length === 0 && <p className="users-note">No maps match.</p>}

      <div className="maps-grid">
        {maps.map((map) => (
          <MapTile key={map.uuid} map={map} onOpen={() => setOpen(map.uuid)} />
        ))}
      </div>
      {cursor && (
        <button className="btn" type="button" onClick={() => void loadMore()}>
          Load 50 more
        </button>
      )}

      {open && (
        <MapDetail
          uuid={open}
          canChange={can(tier, "portal")}
          csrfToken={csrfToken}
          onOpenPlayer={onOpenPlayer}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function MapDetail({
  uuid,
  canChange,
  csrfToken,
  onOpenPlayer,
  onClose,
}: {
  uuid: string;
  canChange: boolean;
  csrfToken: string;
  onOpenPlayer?: (uuid: string) => void;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["map", uuid],
    queryFn: () =>
      getJson<{ map: MapRow; numbers: Array<{ serverId: string; mapId: number; origin: boolean }> }>(`/api/maps/${uuid}`),
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const map = detail.data?.map;

  const change = async (action: "block" | "unblock"): Promise<void> => {
    const ok = await confirmDialog({
      title: action === "block" ? "Block this map?" : "Unblock this map?",
      message:
        action === "block"
          ? "Players will see the red BLOCKED image instead of this art. Online servers switch within about a second."
          : "Players will see the original art again.",
      confirmLabel: action === "block" ? "Block map" : "Unblock map",
      danger: action === "block",
    });
    if (!ok) return;
    setBusy(true);
    setResult(null);
    try {
      const response = await fetch(`/api/maps/${uuid}/${action}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken },
      });
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
      setResult(
        response.ok
          ? { ok: true, text: json.message ?? "Done." }
          : { ok: false, text: response.status === 403 ? "You are not allowed to do that." : json.error ?? `Failed (HTTP ${response.status})` },
      );
      await queryClient.invalidateQueries({ queryKey: ["map", uuid] });
      await queryClient.invalidateQueries({ queryKey: ["maps"] });
    } finally {
      setBusy(false);
    }
  };

  const person = (who: { uuid: string | null; name: string } | null): JSX.Element =>
    who?.uuid && onOpenPlayer ? (
      <button type="button" className="who" onClick={() => onOpenPlayer(who.uuid as string)}>
        {who.name}
      </button>
    ) : (
      <span>{who?.name ?? "Unknown"}</span>
    );

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog map-detail" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Map">
        <header className="profile-card-head">
          <h2>Map</h2>
          <span className="mono users-note">{uuid}</span>
          <span className="grow" />
          <button className="btn" type="button" onClick={onClose}>Close</button>
        </header>
        {detail.error && <div className="login-error">{detail.error instanceof Error ? detail.error.message : "Could not load the map."}</div>}
        {map && (
          <>
            <div className="map-pair">
              {map.blocked ? (
                <>
                  <figure>
                    <BlockedArt size={320} />
                    <figcaption>Now</figcaption>
                  </figure>
                  <figure>
                    <MapImage map={map} size={320} />
                    <figcaption>Before it was blocked</figcaption>
                  </figure>
                </>
              ) : (
                <figure>
                  <MapImage map={map} size={384} />
                </figure>
              )}
            </div>
            <div className="profile-fields">
              <div className="profile-field"><span>Created by</span><b>{person(map.creator)}</b></div>
              <div className="profile-field"><span>Created on</span><b>{map.createdServer ?? "Unknown"}</b></div>
              <div className="profile-field"><span>Created at</span><b>{map.createdAt ? formatTimestamp(map.createdAt) : "Unknown"}</b></div>
              {map.blocked && (
                <>
                  <div className="profile-field"><span>Blocked by</span><b>{person(map.blockedBy)}</b></div>
                  <div className="profile-field"><span>Blocked at</span><b>{map.blockedAt ? formatTimestamp(map.blockedAt) : "Unknown"}</b></div>
                </>
              )}
            </div>
            <h3 className="profile-sub">Map number on each server</h3>
            <p>
              {(detail.data?.numbers ?? []).length === 0
                ? "Unknown"
                : detail.data?.numbers.map((n) => `#${n.mapId} on ${n.serverId}${n.origin ? " (original)" : ""}`).join(" · ")}
            </p>
            {result && <div className={result.ok ? "evidence-ok" : "login-error"}>{result.text}</div>}
            {canChange && (
              <div className="dialog-buttons">
                {map.blocked ? (
                  <button className="btn" type="button" disabled={busy} onClick={() => void change("unblock")}>Unblock</button>
                ) : (
                  <button className="btn" type="button" data-variant="danger" disabled={busy} onClick={() => void change("block")}>Block</button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
